




































































































import { Component, Inject, Vue, Prop } from "vue-property-decorator";
import {
  IUpgradePackagePayload,
  IUpmindUpgradeProviderData,
  IUpmindUpgradeProviderMethods
} from "@/models/providers/upmind";
import { IProduct, IProductOption, IProductPrice } from "@/models/products";
import { IContractProduct } from "@/models/contracts";
import _ from "@/boot/lodash";

@Component
export default class UpmindLicenseCard extends Vue {
  // Inject

  @Inject({ from: "upgradeData" })
  public readonly upgradeData!: () => IUpmindUpgradeProviderData;

  @Inject({ from: "upgradeMethods" })
  public readonly $upgradeMethods!: IUpmindUpgradeProviderMethods;

  // Props

  @Prop({ required: true })
  public readonly license!: IProduct;

  @Prop({ default: false })
  public readonly isDisabled!: boolean;

  // Data

  public isProcessing = false;
  public selectedOptions: Partial<IProductOption>[] = [];

  // Computed

  public get $upgradeData() {
    return this.upgradeData();
  }

  public get optionsByCategory() {
    // Get unique option categories
    const optionCategories = _.orderBy(
      _.uniqBy(this.license.products_options, "category.id"),
      ["attached_order"]
    );
    return _.filter(
      _.map(optionCategories, ({ category }) => {
        // Get category options
        const categoryOptions = _.orderBy(
          _.filter(this.license.products_options, option => {
            return (
              option.category_id === category.id &&
              !!_.find(option.prices, price =>
                [0, 1].includes(price.billing_cycle_months)
              )
            );
          }),
          ["attached_order"]
        );

        return {
          id: category.id,
          name: category.name_translated || category.name,
          description:
            category.short_description_translated || category.short_description,
          multiple: category.multiple,
          required: category.required,
          price_override: category.price_override,
          options: _.map(categoryOptions, option => {
            const price = _.find(option.prices, price =>
              [0, 1].includes(price.billing_cycle_months)
            );
            return {
              id: option.id,
              name: option.name_translated || option.name,
              description: option.description_translated || option.description,
              ..._.pick(price as IProductPrice, [
                "billing_cycle_months",
                "price_formatted"
              ])
            };
          })
        };
      }),
      category => !!category.options.length
    );
  }

  public get mandatoryOptions() {
    return _.filter(this.license.products_options, option => {
      return this.$upgradeData.currentLicenseOptionIds.includes(option.id);
    });
  }

  public get mandatoryOptionIds() {
    return _.map(this.mandatoryOptions, "id");
  }

  public get selectedOptionIds() {
    return _.map(this.selectedOptions, "id");
  }

  public get isCurrentPlan() {
    return (
      this.license.code ===
      (this.$store.getters["org/currentLicense"] as IContractProduct)?.product
        ?.code
    );
  }

  public get isActionable() {
    if (this.isCurrentPlan)
      return this.selectedOptionIds.length > this.mandatoryOptionIds.length;
    return true;
  }

  public get licenseName() {
    return this.license.name_translated || this.license.name;
  }

  public get upgradePayload(): IUpgradePackagePayload {
    return {
      product: {
        product_id: this.license.id,
        billing_cycle_months: this.license.billing_cycle_months
      },
      options: _.map(this.selectedOptions as IProductOption[], option => ({
        product_id: option.id,
        billing_cycle_months: option.billing_cycle_months
      }))
    };
  }

  // Life-cycle methods

  public created() {
    this.selectedOptions = [...this.mandatoryOptions];
  }

  // Methods

  public toggleOption(option: Partial<IProductOption>, isChecked: boolean) {
    this.selectedOptions = isChecked
      ? _.unionBy([option], this.selectedOptions, "id")
      : _.filter(this.selectedOptions, ({ id }) => id !== option.id);
  }

  public async upgradePackage() {
    try {
      this.isProcessing = true;
      await this.$upgradeMethods.confirmUpgrade(
        this.upgradePayload,
        this.licenseName
      );
    } finally {
      this.isProcessing = false;
    }
  }
}
